/**
 * Boli Live - Auth Store (Zustand)
 *
 * Global authentication state management.
 * Wraps @boli/core AuthService for web-specific state.
 */

import { create } from 'zustand';
import { getAuthService } from '@boli/core';
import type { User, KYCStatus } from '@boli/types';

interface AuthState {
  // State
  user: (User & { role?: string }) | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  kycStatus: KYCStatus | null;

  // Computed role properties
  isAdmin: boolean;
  isModerator: boolean;

  // Actions
  initialize: () => Promise<void>;
  signInWithGoogle: (idToken: string) => Promise<boolean>;
  signInWithEmail: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  refreshKYCStatus: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  kycStatus: null,
  isAdmin: false,
  isModerator: false,

  // Initialize auth state
  initialize: async () => {
    set({ isLoading: true });

    try {
      const authService = getAuthService();

      // Listen to auth state changes
      authService.onAuthStateChange((user: User | null) => {
        set({
          user,
          isAuthenticated: user !== null,
        });
      });

      // Get current user
      const user = await authService.getCurrentUser();

      set({
        user,
        isAuthenticated: user !== null,
        isInitialized: true,
        isLoading: false,
      });

      // Fetch KYC status if authenticated
      if (user) {
        get().refreshKYCStatus();
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      set({
        user: null,
        isAuthenticated: false,
        isInitialized: true,
        isLoading: false,
      });
    }
  },

  // Sign in with Google
  signInWithGoogle: async (idToken: string) => {
    set({ isLoading: true });

    try {
      const authService = getAuthService();
      const result = await authService.signInWithGoogle(idToken);

      if (result.success && result.data) {
        set({
          user: result.data.user,
          isAuthenticated: true,
          isLoading: false,
        });

        // Fetch KYC status
        get().refreshKYCStatus();

        return true;
      } else {
        set({ isLoading: false });
        return false;
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      set({ isLoading: false });
      return false;
    }
  },

  // Sign in with Email/Password (for testing)
  signInWithEmail: async (email: string, password: string) => {
    set({ isLoading: true });

    try {
      // Use dynamic API URL based on environment
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8090';
      const response = await fetch(`${apiBaseUrl}/api/collections/users/auth-with-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identity: email, password }),
      });

      if (!response.ok) {
        set({ isLoading: false });
        return false;
      }

      const data = await response.json();

      // Store token in localStorage for other API calls
      localStorage.setItem('boli_auth_token', data.token);
      localStorage.setItem('boli_user', JSON.stringify(data.record));
      localStorage.setItem('boli_expires_at', (Date.now() + 7 * 24 * 60 * 60 * 1000).toString());

      // Map PocketBase user to our User type with role
      const userRole = data.record.role || 'user';
      const user: User & { role?: string } = {
        id: data.record.id,
        email: data.record.email,
        firstName: data.record.name?.split(' ')[0] || 'Test',
        lastName: data.record.name?.split(' ')[1] || 'User',
        cnicVerified: false,
        logisticsScore: 100,
        isShadowBanned: false,
        createdAt: data.record.created,
        updatedAt: data.record.updated || data.record.created,
        role: userRole,
      };

      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        isAdmin: userRole === 'admin',
        isModerator: userRole === 'admin' || userRole === 'moderator',
      });

      get().refreshKYCStatus();
      return true;
    } catch (error) {
      console.error('Email sign in failed:', error);
      set({ isLoading: false });
      return false;
    }
  },

  // Sign out
  signOut: async () => {
    set({ isLoading: true });

    try {
      const authService = getAuthService();
      await authService.signOut();

      set({
        user: null,
        isAuthenticated: false,
        kycStatus: null,
        isLoading: false,
        isAdmin: false,
        isModerator: false,
      });
    } catch (error) {
      console.error('Sign out failed:', error);
      set({ isLoading: false });
    }
  },

  // Refresh KYC status
  refreshKYCStatus: async () => {
    try {
      const authService = getAuthService();
      const kycStatus = await authService.getKYCStatus();
      set({ kycStatus });
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
    }
  },
}));
